import { State } from 'jumpstate'
import { shallowEqual } from 'webmango-utils'
import merge from 'lodash/merge'
import pickBy from 'lodash/pickBy'
import get from 'lodash/get'
import setWith from 'lodash/setWith'
import updateWith from 'lodash/updateWith'
import unset from 'lodash/unset'

const _copy = (value, key, obj) => ({ ...obj[key] })
const setValue = (obj, key, value) => {
  return setWith(obj, key, value, _copy)
}

const updateValue = (obj, key, fn) => {
  return updateWith(obj, key, fn, _copy)
}

const getKV = (kv = {}) => {
  if (Array.isArray(kv)) return kv
  return [kv.key, kv.value]
}

export default function ValueState (name, initial = {}, extend = {}) {
  if (!name) throw new Error('name required')
  const api = State(name, {
    initial,
    on (prevState, key) {
      const nextState = setValue({ ...prevState }, key, true)
      if (shallowEqual(nextState, prevState)) return prevState
      return nextState
    },

    off (prevState, key) {
      const nextState = setValue({ ...prevState }, key, false)
      if (shallowEqual(nextState, prevState)) return prevState
      return nextState
    },

    toggle (state, key) {
      return updateValue({ ...state }, key, v => !v)
    },

    set (prevState, kv) {
      const [ key, value ] = getKV(kv)
      const nextState = setValue({ ...prevState }, key, value)
      if (shallowEqual(nextState, prevState)) return prevState
      return nextState
    },

    replaceState (prevState, nextState) {
      if (shallowEqual(nextState, prevState)) return prevState
      return nextState
    },

    merge (prevState, kv) {
      const [ key, value ] = getKV(kv)
      const nextState = updateValue({ ...prevState }, key, v => ({ ...v, ...value }))
      if (shallowEqual(nextState, prevState)) return prevState
      return nextState
    },

    incr (state, key) {
      return updateValue({ ...state }, key, (v = 0) => v + 1)
    },

    decr (state, key) {
      return updateValue({ ...state }, key, (v = 0) => v - 1)
    },

    clear (prevState) {
      const nextState = {}
      if (shallowEqual(nextState, prevState)) return prevState
      return nextState
    },

    delete (prevState, key) {
      const nextState = setValue({ ...prevState }, key, undefined)
      unset(nextState, key)
      if (shallowEqual(nextState, prevState)) return prevState
      return nextState
    },

    deleteIfEmpty (prevState, key) {
      const prevValue = get(prevState, key)
      if (!prevValue || (prevValue && !Object.keys(prevValue).length)) {
        const nextState = setValue({ ...prevState }, key, undefined)
        unset(nextState, key)
        if (shallowEqual(nextState, prevState)) return prevState
        return nextState
      } else {
        return prevState
      }
    },

    mergeItems (prevState, newItems) {
      const nextState = merge({},
        prevState,
        newItems.reduce((o, item) => {
          const { id } = item
          return Object.assign(o, {
            [id]: item
          })
        }, {})
      )
      if (shallowEqual(nextState, prevState)) return prevState
      return nextState
    },

    addItems (prevState, newItems) {
      const nextState = {
        ...prevState,
        ...newItems.reduce((o, item) => {
          const { id } = item
          return Object.assign(o, {
            [id]: item
          })
        }, {})
      }
      if (shallowEqual(nextState, prevState)) return prevState
      return nextState
    },

    setState (prevState, update) {
      const updateState = typeof update === 'function' ? update(prevState) : update

      const nextState = pickBy({
        ...prevState,
        ...updateState
      }, v => v !== undefined)

      if (shallowEqual(nextState, prevState)) return prevState
      return nextState
    },

    put (prevState, payload) {
      const { id } = payload
      const nextState = {
        ...prevState,
        [id]: payload
      }

      if (shallowEqual(nextState, prevState)) return prevState
      return nextState
    },

    remove (prevState, payload) {
      const id = payload.id || payload._id || payload
      if (!prevState[id]) return prevState // ignore if item not exists
      const nextState = Object.assign({}, prevState)
      delete nextState[id]
      if (shallowEqual(nextState, prevState)) return prevState
      return nextState
    },

    initialise (state, payload) {
      const nextState = Object.assign({}, state)
      let changes = 0
      payload.forEach(data => {
        const id = data.id || data._id
        if (data._deleted) {
          // ignore deleted items not in state
          if (!state[id]) return
          // remove deleted items
          delete nextState[id]
        } else {
          // update/add item
          nextState[id] = Object.assign({}, data || {}, { id })
        }
        changes++
      })
      if (!changes) return state // if no changes return same state
      return nextState
    },
    ...extend
  })

  // aliases
  Object.assign(api, {
    enable: api.on,
    inc: api.incr,
    dec: api.decr,
    disable: api.off,
    unset: api.delete
  })

  return api
}



// WEBPACK FOOTER //
// ../webmango-core/state/lib/ValueState.js